import { useMemo } from 'react';
import { useTheme } from '@emotion/react';
import { CoreState, DeploymentState } from 'common/CommonConstants';
import { StatusItemState, StatusItemStateTexts } from 'common/status/StatusDocument';
import type { StatusTreeContainerItem } from 'common/status/StatusTree';
import type { StatusTreeTagIconProps } from 'client/components/StatusTree/components/StatusTreeTagIcon';
import type { TagProps } from 'client/components/Tag';

export type FigureState = 'none' | 'normal' | 'success' | 'warning' | 'error' | 'fatal' | 'loading' | 'disabled';

export type DeploymentCombinedState = {
  deploymentStateID?: DeploymentState | null;
  coreStateID?: CoreState | null;
  itemStateID?: StatusItemState;
};

export type StatusIcon = 'check' | 'toggle' | 'clone' | 'delete' | 'loading' | 'service' | 'warning' | 'error' | 'fatal' | 'none';
export type StatusColor = 'none' | 'red' | 'yellow' | 'green' | 'gray';

export interface StatusFeatures {
  icon: StatusIcon;
  color: StatusColor;
  isEnabled: boolean;
  isToggleable: boolean;
}

const features = (icon: StatusIcon, color: StatusColor, extras: ('enabled' | 'enable-toggle')[] = []) => {
  const isEnabled = extras.includes('enabled');
  const isToggleable = extras.includes('enable-toggle');
  return { icon, color, isEnabled, isToggleable };
};

const figureStateToStatusFeaturesMap: Record<FigureState, StatusFeatures> = {
  none: features('none', 'none'),
  normal: features('none', 'none'),
  success: features('check', 'green'),
  warning: features('warning', 'yellow'),
  error: features('error', 'red'),
  fatal: features('fatal', 'red'),
  loading: features('loading', 'gray'),
  disabled: features('none', 'gray'),
};

export const figureStateToStatusFeatures = (state: FigureState) => figureStateToStatusFeaturesMap[state];

/** Convert combination of deployment/core states to UX icons + enabled buttons states and what API functions should be possible */
export const deploymentCombinedStateToStatusFeatures = (state: DeploymentCombinedState): StatusFeatures => {
  const { deploymentStateID } = state;

  const coreStateID = state.coreStateID ?? CoreState.RUNNING;
  const itemStateID = state.itemStateID ?? StatusItemState.GRAY;

  if (deploymentStateID === DeploymentState.STOPPED) {
    if ([CoreState.DELETED_PENDING].includes(coreStateID)) return features('toggle', 'gray');
    return features('toggle', 'gray', ['enable-toggle']);
  }

  if (deploymentStateID === DeploymentState.CLONE_PENDING) {
    if ([CoreState.INITIALIZING].includes(coreStateID)) return features('clone', 'yellow', ['enabled']);
    if ([CoreState.INITIALIZING_ERROR].includes(coreStateID)) return features('service', 'red', ['enabled']);
    return features('fatal', 'red');
  }

  if (deploymentStateID === DeploymentState.RUNNING) {
    if ([CoreState.INITIALIZING].includes(coreStateID)) return features('loading', 'yellow', ['enabled']);
    if ([CoreState.RUNNING_CONNECTING].includes(coreStateID)) return features('loading', 'yellow', ['enabled', 'enable-toggle']);
    if ([CoreState.INITIALIZING_ERROR].includes(coreStateID)) return features('service', 'red', ['enabled']);
    if ([CoreState.RUNNING_ERROR].includes(coreStateID)) return features('service', 'red', ['enabled', 'enable-toggle']);
    if ([CoreState.RUNNING].includes(coreStateID)) {
      let icon: StatusIcon = 'none';
      if (itemStateID == StatusItemState.RED) icon = 'error';
      if (itemStateID == StatusItemState.YELLOW) icon = 'warning';
      return features(icon, StatusItemStateTexts[itemStateID], ['enabled', 'enable-toggle']);
    }
    return features('fatal', 'red');
  }

  if (deploymentStateID === DeploymentState.DELETED_PENDING) return features('delete', 'yellow');
  if (deploymentStateID === DeploymentState.DELETED) return features('delete', 'yellow');
  if (deploymentStateID === DeploymentState.DELETED_ERROR) return features('service', 'red');

  return features('fatal', 'red');
};

export const getFeaturesFromState = (state: FigureState | DeploymentCombinedState | null): StatusFeatures => {
  if (!state) return figureStateToStatusFeatures('normal');
  if (typeof state === 'string') return figureStateToStatusFeatures(state);
  return deploymentCombinedStateToStatusFeatures(state);
};

export const useStatusColorToHex = () => {
  const theme = useTheme();

  return useMemo<Record<StatusColor, string>>(
    () => ({
      none: theme.color.primary[100],
      red: theme.color.red[100],
      green: theme.color.green[100],
      yellow: theme.color.yellow[100],
      gray: theme.color.grey[100],
    }),
    [theme]
  );
};

export const useFigureStateToColor = () => {
  const theme = useTheme();

  return useMemo<Record<FigureState, string>>(
    () => ({
      none: theme.color.primary[100],
      normal: theme.color.green[100],
      success: theme.color.green[100],
      warning: theme.color.yellow[100],
      error: theme.color.red[100],
      fatal: theme.color.red[100],
      loading: theme.color.grey[100],
      disabled: theme.color.grey[100],
    }),
    [theme]
  );
};

export const useStatusItemStateToColor = () => {
  const theme = useTheme();

  return useMemo(
    () => ({
      [StatusItemState.GREEN]: theme.color.green,
      [StatusItemState.YELLOW]: theme.color.yellow,
      [StatusItemState.RED]: theme.color.red,
      [StatusItemState.GRAY]: theme.color.grey,
    }),
    [theme]
  );
};

export const statusItemStateToTagProps: Record<StatusItemState, Partial<TagProps>> = {
  [StatusItemState.GREEN]: { type: 'success' },
  [StatusItemState.YELLOW]: { type: 'warning' },
  [StatusItemState.RED]: { type: 'error' },
  [StatusItemState.GRAY]: { type: 'grey' },
};

export const containerStateToTagProps: Record<StatusTreeContainerItem['state'], Partial<TagProps>> = {
  running: { type: 'success' },
  waiting: { type: 'warning' },
  terminated: { type: 'grey' },
};

export const containerStateToStatusIconProps: Record<StatusTreeContainerItem['state'], StatusTreeTagIconProps> = {
  running: { itemStateID: StatusItemState.GREEN, isReady: true },
  waiting: { itemStateID: StatusItemState.YELLOW, isReady: false },
  terminated: { itemStateID: StatusItemState.GRAY, isReady: true },
};

export const containerStateToText: Record<StatusTreeContainerItem['state'], string> = {
  running: 'Running',
  waiting: 'Waiting',
  terminated: 'Terminated',
};
